import { useApolloClient, useMutation } from '@apollo/client';
import {
  ArrowUturnLeftIcon,
  InboxArrowDownIcon,
  NoSymbolIcon,
  TrashIcon,
} from '@heroicons/react/20/solid';
import { ACTION_USER_RESEND_INVITE } from '@retainerclub/admin-api';
import {
  useApp,
  useAuth,
  useElevatedAccess,
  useToast,
} from '@retainerclub/hooks';
import {
  DESTROY_PARTNER_USER,
  DISCARD_PARTNER_USER,
  RESTORE_PARTNER_USER,
} from '@retainerclub/shared-api';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { SelectMenuItem } from '../../../../shared';

function PartnerUsersShowActions({ partnerUser }) {
  const client = useApolloClient();
  const { isAdmin, isPartner } = useApp();
  const { currentUser } = useAuth();
  const { isSuperAdmin } = useElevatedAccess();
  const { setToast } = useToast();
  const navigate = useNavigate();
  const [sendingInvite, setSendingInvite] = useState(false);

  const [destroy] = useMutation(DESTROY_PARTNER_USER);
  const [discard] = useMutation(DISCARD_PARTNER_USER);
  const [restore] = useMutation(RESTORE_PARTNER_USER);

  // Hard delete is reserved for the "safe" (data-free) case. Admin Super Admins may
  // delete any data-free user; partner Org/Location Admins may delete pending users
  // (which have no data). Users with data are deactivated (soft delete) instead.
  // roleLevel 0 = Org Admin, 10 = Location Admin (20 = Location User).
  const isPartnerAdmin =
    isPartner &&
    (currentUser?.roleLevel === 0 || currentUser?.roleLevel === 10);
  const canDelete =
    (isSuperAdmin && partnerUser?.deletable === true) ||
    (isPartnerAdmin && partnerUser?.state === 'pending');

  // Soft delete (deactivate) / restore are admin-platform, Super Admin only.
  // Pending users are removed via delete, not deactivate.
  const canDeactivate =
    isAdmin &&
    isSuperAdmin &&
    partnerUser?.discarded !== true &&
    partnerUser?.state !== 'pending';
  const canRestore = isAdmin && isSuperAdmin && partnerUser?.discarded === true;

  async function handleDestroyPartnerUser() {
    if (
      !window.confirm(
        `Permanently delete ${partnerUser.firstName} ${partnerUser.lastName}? This cannot be undone.`,
      )
    ) {
      return;
    }

    try {
      await destroy({
        variables: {
          input: {
            id: partnerUser.id,
            clientMutationId: crypto.randomUUID(),
          },
        },
        update(cache) {
          cache.evict({
            id: cache.identify({
              __typename: 'PartnerUser',
              id: partnerUser.id,
            }),
          });
          cache.gc();
        },
      });

      setToast({
        show: true,
        title: 'Success',
        message: 'Partner User has been deleted',
        type: 'success',
      });

      navigate(isAdmin ? '/partners/users' : '/users');
    } catch (err) {
      console.error(err);
      setToast({
        show: true,
        title: 'Error',
        message: "We couldn't delete this Partner User. Please try again later",
        type: 'error',
      });
    }
  }

  async function handleDiscardPartnerUser() {
    if (
      !window.confirm(
        `Deactivate ${partnerUser.firstName} ${partnerUser.lastName}? They will lose access and be hidden from partner views. You can restore them later.`,
      )
    ) {
      return;
    }

    try {
      // The mutation returns the updated PartnerUser, so Apollo normalizes the
      // new discarded/state values into the cache automatically.
      await discard({
        variables: {
          input: {
            id: partnerUser.id,
            clientMutationId: crypto.randomUUID(),
          },
        },
      });

      setToast({
        show: true,
        title: 'Success',
        message: 'Partner User has been deactivated',
        type: 'success',
      });
    } catch (err) {
      console.error(err);
      setToast({
        show: true,
        title: 'Error',
        message:
          "We couldn't deactivate this Partner User. Please try again later",
        type: 'error',
      });
    }
  }

  async function handleRestorePartnerUser() {
    if (
      !window.confirm(
        `Restore ${partnerUser.firstName} ${partnerUser.lastName}? They will regain access.`,
      )
    ) {
      return;
    }

    try {
      await restore({
        variables: {
          input: {
            id: partnerUser.id,
            clientMutationId: crypto.randomUUID(),
          },
        },
      });

      setToast({
        show: true,
        title: 'Success',
        message: 'Partner User has been restored',
        type: 'success',
      });
    } catch (err) {
      console.error(err);
      setToast({
        show: true,
        title: 'Error',
        message:
          "We couldn't restore this Partner User. Please try again later",
        type: 'error',
      });
    }
  }

  async function resendInvite() {
    if (sendingInvite) {
      return;
    }
    setSendingInvite(true);

    try {
      await client.mutate({
        mutation: ACTION_USER_RESEND_INVITE,
        variables: {
          input: {
            input: {
              id: partnerUser.id,
              klass: 'PartnerUser',
            },
            clientMutationId: crypto.randomUUID(),
          },
        },
      });

      setToast({
        show: true,
        title: 'Success',
        message: 'Invite has been resent to the Partner User',
        type: 'success',
      });
    } catch (error) {
      console.error(error);
      setToast({
        show: true,
        title: 'Error',
        message: "We couldn't resend the invite. Please try again later",
        type: 'error',
      });
    } finally {
      setSendingInvite(false);
    }
  }

  return (
    <>
      {canDelete && (
        <div className="py-1">
          <SelectMenuItem
            onClick={() => handleDestroyPartnerUser()}
            icon={TrashIcon}
          >
            Delete User
          </SelectMenuItem>
        </div>
      )}

      {canDeactivate && (
        <div className="py-1">
          <SelectMenuItem
            onClick={() => handleDiscardPartnerUser()}
            icon={NoSymbolIcon}
          >
            Deactivate User
          </SelectMenuItem>
        </div>
      )}

      {canRestore && (
        <div className="py-1">
          <SelectMenuItem
            onClick={() => handleRestorePartnerUser()}
            icon={ArrowUturnLeftIcon}
          >
            Restore User
          </SelectMenuItem>
        </div>
      )}

      {isAdmin && partnerUser?.state === 'pending' && (
        <div className="py-1">
          <SelectMenuItem
            onClick={() => resendInvite()}
            icon={InboxArrowDownIcon}
            disabled={sendingInvite}
          >
            Resend Invite
          </SelectMenuItem>
        </div>
      )}
    </>
  );
}

PartnerUsersShowActions.propTypes = {
  partnerUser: PropTypes.shape({
    id: PropTypes.string.isRequired,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    state: PropTypes.string,
    deletable: PropTypes.bool,
    discarded: PropTypes.bool,
  }).isRequired,
};

export default PartnerUsersShowActions;
